import React, { useEffect, useState } from 'react';
import usePaymentStore from '../store/paymentStore';
import useProjectStore from '../store/projectStore';
import useUIStore from '../store/uiStore';
import { getEmployees } from '../services/employeeService';
import PaymentsBarChart from '../components/PaymentsBarChart';
import { formatDateDDMMYY } from '../utils/formatDate';
import ScrollX from '../components/ScrollX';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const AGING_LABELS = {
  current: 'Current',
  '0-30': '0-30 days',
  '31-60': '31-60 days',
  '61-90': '61-90 days',
  '90+': '90+ days',
};

const AGING_TONE = {
  current: 'green',
  '0-30': 'amber',
  '31-60': 'amber',
  '61-90': 'red',
  '90+': 'red',
};

const STATUS_COLORS = {
  open: 'bg-gray-200 text-gray-800',
  partially_paid: 'bg-amber-100 text-amber-800',
  paid: 'bg-green-100 text-green-800',
  written_off: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS = {
  open: 'Open',
  partially_paid: 'Partial',
  paid: 'Paid',
  written_off: 'Written off',
};

const AGING_BADGE = {
  current: 'bg-green-100 text-green-800',
  '0-30': 'bg-amber-100 text-amber-800',
  '31-60': 'bg-amber-100 text-amber-800',
  '61-90': 'bg-red-100 text-red-800',
  '90+': 'bg-red-100 text-red-800',
};

const StatCard = ({ label, value, tone = 'gray' }) => {
  const tones = {
    gray: 'bg-gray-50 text-gray-900',
    red: 'bg-red-50 text-red-700',
    blue: 'bg-blue-50 text-blue-700',
  };
  return (
    <div className={`rounded p-4 ${tones[tone]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
};

const initialForm = {
  debtor_name: '', project_id: '', invoice_number: '', due_date: '', total_amount: '', amount_received: '', responsible_employee_id: '', notes: '',
};

const PaymentsPage = () => {
  const { payments, dashboard, isLoading, error, fetchPayments, fetchDashboard, createPayment, updatePayment, deletePayment } = usePaymentStore();
  const { projects, fetchProjects } = useProjectStore();
  const showToast = useUIStore((s) => s.showToast);

  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [view, setView] = useState('sheet'); // 'sheet' | 'graph'
  const [statusFilter, setStatusFilter] = useState('');
  const [responsibleFilter, setResponsibleFilter] = useState('');
  const [agingFilter, setAgingFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [promiseEditId, setPromiseEditId] = useState(null);
  const [promiseValue, setPromiseValue] = useState('');

  const load = (overrides = {}) => {
    const params = {
      status: statusFilter,
      responsible_employee_id: responsibleFilter,
      aging_bucket: agingFilter,
      ...overrides,
    };
    const cleanParams = {};
    Object.entries(params).forEach(([k, v]) => { if (v) cleanParams[k] = v; });
    fetchPayments(cleanParams);
  };

  useEffect(() => {
    load();
    fetchDashboard();
    fetchProjects();
    getEmployees().then((res) => setEmployees(res.data.employees || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleStatusChange = (e) => { setStatusFilter(e.target.value); load({ status: e.target.value }); };
  const handleResponsibleChange = (e) => { setResponsibleFilter(e.target.value); load({ responsible_employee_id: e.target.value }); };
  const handleAgingChange = (e) => { setAgingFilter(e.target.value); load({ aging_bucket: e.target.value }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.debtor_name || !form.due_date || !form.total_amount) {
      showToast('Debtor, due date, and total amount are required', 'error');
      return;
    }
    const result = await createPayment({
      debtor_name: form.debtor_name,
      project_id: form.project_id ? Number(form.project_id) : null,
      invoice_number: form.invoice_number || null,
      due_date: form.due_date,
      total_amount: Number(form.total_amount),
      amount_received: form.amount_received ? Number(form.amount_received) : 0,
      responsible_employee_id: form.responsible_employee_id ? Number(form.responsible_employee_id) : null,
      notes: form.notes || null,
    });
    if (result.success) {
      showToast('Payment recorded', 'success');
      setForm(initialForm);
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleRecordPayment = async (payment) => {
    const amount = window.prompt(`Amount received from ${payment.debtor_name} (pending: ${money(payment.pending_amount)}):`);
    if (!amount) return;
    const additional = Number(amount);
    if (!additional || additional <= 0) {
      showToast('Enter a valid amount', 'error');
      return;
    }
    const result = await updatePayment(payment.id, { amount_received: payment.amount_received + additional });
    if (result.success) showToast('Payment updated', 'success');
  };

  const handleWriteOff = async (payment) => {
    if (!window.confirm(`Write off the remaining ${money(payment.pending_amount)} owed by ${payment.debtor_name}?`)) return;
    const result = await updatePayment(payment.id, { is_written_off: true });
    if (result.success) showToast('Marked written off', 'success');
  };

  const handleDelete = async (payment) => {
    if (!window.confirm(`Remove this payment record for ${payment.debtor_name}?`)) return;
    const result = await deletePayment(payment.id);
    if (result.success) showToast('Payment removed', 'success');
  };

  // Local calendar date, not toISOString() - that is UTC and would show
  // yesterday as "today" for the first hours of an Indian morning.
  const todayLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const startPromise = (p) => { setPromiseEditId(p.id); setPromiseValue(p.promised_date && p.promised_date >= todayLocal() ? p.promised_date : ''); };

  const savePromise = async (p, value) => {
    const result = await updatePayment(p.id, { promised_date: value || null });
    if (result.success) {
      showToast(value ? 'Promised date saved' : 'Promised date cleared', 'success');
      setPromiseEditId(null);
    }
  };

  const renderPromise = (p) => {
    if (p.status === 'paid' || p.status === 'written_off') return <span className="text-gray-300">—</span>;
    if (promiseEditId === p.id) {
      return (
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            min={todayLocal()}
            value={promiseValue}
            onChange={(ev) => setPromiseValue(ev.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-xs"
          />
          <button disabled={!promiseValue} onClick={() => savePromise(p, promiseValue)} className="text-green-700 hover:underline text-xs font-semibold disabled:opacity-40">Save</button>
          {p.promised_date && <button onClick={() => savePromise(p, null)} className="text-red-600 hover:underline text-xs font-semibold">Clear</button>}
          <button onClick={() => setPromiseEditId(null)} className="text-gray-500 hover:underline text-xs">✕</button>
        </div>
      );
    }
    if (p.promise_status === 'missed') {
      return <button onClick={() => startPromise(p)} title="Promise missed - click to set a new date" className="text-xs px-2 py-1 rounded font-semibold bg-red-100 text-red-800 whitespace-nowrap">Missed {formatDateDDMMYY(p.promised_date)}</button>;
    }
    if (p.promise_status === 'promised') {
      return <button onClick={() => startPromise(p)} title="Click to change" className="text-xs px-2 py-1 rounded font-semibold bg-green-100 text-green-800 whitespace-nowrap">Promised {formatDateDDMMYY(p.promised_date)}</button>;
    }
    return <button onClick={() => startPromise(p)} className="text-komal-blue hover:underline text-xs font-semibold whitespace-nowrap">+ Set date</button>;
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({
      debtor_name: p.debtor_name,
      due_date: p.due_date,
      total_amount: p.total_amount,
      responsible_employee_id: p.responsible_employee_id || '',
      notes: p.notes || '',
    });
  };

  const saveEdit = async (id) => {
    const result = await updatePayment(id, {
      debtor_name: editForm.debtor_name,
      due_date: editForm.due_date,
      total_amount: Number(editForm.total_amount),
      responsible_employee_id: editForm.responsible_employee_id ? Number(editForm.responsible_employee_id) : null,
      notes: editForm.notes || null,
    });
    if (result.success) {
      showToast('Payment updated', 'success');
      setEditingId(null);
    }
  };

  const handleDownloadCsv = () => {
    const headers = ['Debtor', 'Remarks', 'Project', 'Invoice #', 'Due Date', 'Total', 'Received', 'Pending', 'Days Overdue', 'Aging', 'Promised', 'Responsible', 'Status'];
    const rows = payments.map((p) => [
      p.debtor_name, p.notes || '', p.project_name || '', p.invoice_number || '', p.due_date,
      p.total_amount, p.amount_received, p.pending_amount, p.days_overdue,
      AGING_LABELS[p.aging_bucket], p.promised_date ? `${p.promised_date}${p.promise_status === 'missed' ? ' (missed)' : ''}` : '', p.responsible_employee_name || '', STATUS_LABELS[p.status],
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const byResponsibleBars = (dashboard?.by_responsible || []).map((e) => ({ label: e.name, value: e.pending_amount, tone: 'blue' }));
  const byAgingBars = Object.entries(dashboard?.aging_breakdown || {}).map(([bucket, v]) => ({
    label: AGING_LABELS[bucket], value: v.amount, tone: AGING_TONE[bucket],
  }));

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-6">Payments</h1>

        {dashboard && (
          <div className="glass-card p-6 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <StatCard label="Total pending" value={money(dashboard.total_pending)} />
              <StatCard label="Total overdue" value={money(dashboard.total_overdue)} tone="red" />
              <StatCard label="Open invoices" value={dashboard.open_count} tone="blue" />
              <StatCard label="Debtors" value={dashboard.debtor_count} />
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-semibold text-gray-500 uppercase">Aging</div>
              <div className="flex gap-1">
                <button
                  onClick={() => setView('sheet')}
                  className={`text-xs px-3 py-1 rounded border ${view === 'sheet' ? 'bg-komal-blue text-white border-komal-blue' : 'border-gray-300 text-gray-600'}`}
                >
                  Sheet
                </button>
                <button
                  onClick={() => setView('graph')}
                  className={`text-xs px-3 py-1 rounded border ${view === 'graph' ? 'bg-komal-blue text-white border-komal-blue' : 'border-gray-300 text-gray-600'}`}
                >
                  Graph
                </button>
              </div>
            </div>

            {view === 'sheet' ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5">
                  {Object.entries(dashboard.aging_breakdown).map(([bucket, v]) => (
                    <div key={bucket} className={`rounded p-2.5 text-center ${AGING_BADGE[bucket]}`}>
                      <div className="text-[11px]">{AGING_LABELS[bucket]}</div>
                      <div className="text-sm font-semibold mt-0.5">{money(v.amount)}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded p-4">
                    <div className="text-xs text-gray-500 mb-2">By responsible</div>
                    {dashboard.by_responsible.length === 0 ? (
                      <p className="text-xs text-gray-400">Nothing pending.</p>
                    ) : dashboard.by_responsible.map((e) => (
                      <div key={e.name} className="flex justify-between text-sm py-1 border-b border-gray-50 last:border-0">
                        <span>{e.name}</span>
                        <span className="font-medium">{money(e.pending_amount)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border border-gray-200 rounded p-4">
                    <div className="text-xs text-gray-500 mb-2">Top debtors</div>
                    {dashboard.top_debtors.length === 0 ? (
                      <p className="text-xs text-gray-400">Nothing pending.</p>
                    ) : dashboard.top_debtors.map((e) => (
                      <div key={e.debtor_name} className="flex justify-between text-sm py-1 border-b border-gray-50 last:border-0">
                        <span>{e.debtor_name}</span>
                        <span className="font-medium">{money(e.pending_amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <PaymentsBarChart title="Pending amount by responsible" bars={byResponsibleBars} />
                <PaymentsBarChart title="Pending amount by days overdue" bars={byAgingBars} />
              </div>
            )}
          </div>
        )}

        <div className="glass-card p-6 mb-6">
          <button onClick={() => setShowCreateForm(!showCreateForm)} className="text-sm font-semibold text-komal-blue hover:underline">
            {showCreateForm ? 'Hide' : '+ Record a payment'}
          </button>
          {showCreateForm && (
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Debtor *</label>
                <input
                  required
                  value={form.debtor_name}
                  onChange={(e) => setForm({ ...form, debtor_name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                <select
                  value={form.project_id}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Not tied to a project</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice #</label>
                <input
                  value={form.invoice_number}
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due date *</label>
                <input
                  required
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total amount (₹) *</label>
                <input
                  required
                  type="number"
                  value={form.total_amount}
                  onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Already received (₹)</label>
                <input
                  type="number"
                  value={form.amount_received}
                  onChange={(e) => setForm({ ...form, amount_received: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Responsible</label>
                <select
                  value={form.responsible_employee_id}
                  onChange={(e) => setForm({ ...form, responsible_employee_id: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Unassigned</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div className="col-span-2">
                <button type="submit" className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">
                  Record Payment
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-semibold text-gray-900">Sheet ({payments.length})</h2>
            <div className="flex items-center gap-2">
              <select value={statusFilter} onChange={handleStatusChange} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
                <option value="">All statuses</option>
                <option value="open">Open</option>
                <option value="partially_paid">Partially paid</option>
                <option value="paid">Paid</option>
                <option value="written_off">Written off</option>
              </select>
              <select value={responsibleFilter} onChange={handleResponsibleChange} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
                <option value="">All responsible</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
              <select value={agingFilter} onChange={handleAgingChange} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
                <option value="">All aging</option>
                {Object.entries(AGING_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
              <button onClick={handleDownloadCsv} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 whitespace-nowrap">
                Download CSV
              </button>
            </div>
          </div>

          {isLoading ? (
            <p className="text-gray-500 text-sm p-5">Loading...</p>
          ) : payments.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No payments found.</p>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-medium">Debtor</th>
                  <th className="px-4 py-2 font-medium">Remarks</th>
                  <th className="px-4 py-2 font-medium">Project</th>
                  <th className="px-4 py-2 font-medium">Due</th>
                  <th className="px-4 py-2 font-medium">Total</th>
                  <th className="px-4 py-2 font-medium">Pending</th>
                  <th className="px-4 py-2 font-medium">Aging</th>
                  <th className="px-4 py-2 font-medium">Promised</th>
                  <th className="px-4 py-2 font-medium">Responsible</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  editingId === p.id ? (
                    <tr key={p.id} className="border-b border-gray-50 bg-gray-50">
                      <td className="px-4 py-2">
                        <input
                          value={editForm.debtor_name}
                          onChange={(ev) => setEditForm({ ...editForm, debtor_name: ev.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          value={editForm.notes}
                          onChange={(ev) => setEditForm({ ...editForm, notes: ev.target.value })}
                          placeholder="Remarks"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2 text-gray-400">{p.project_name || '—'}</td>
                      <td className="px-4 py-2">
                        <input
                          type="date"
                          value={editForm.due_date}
                          onChange={(ev) => setEditForm({ ...editForm, due_date: ev.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={editForm.total_amount}
                          onChange={(ev) => setEditForm({ ...editForm, total_amount: ev.target.value })}
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2 text-gray-400">{money(p.pending_amount)}</td>
                      <td className="px-4 py-2 text-gray-400">—</td>
                      <td className="px-4 py-2 text-gray-400">—</td>
                      <td className="px-4 py-2">
                        <select
                          value={editForm.responsible_employee_id}
                          onChange={(ev) => setEditForm({ ...editForm, responsible_employee_id: ev.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                          <option value="">Unassigned</option>
                          {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-gray-400">—</td>
                      <td className="px-4 py-2 space-x-2 whitespace-nowrap">
                        <button onClick={() => saveEdit(p.id)} className="text-green-700 hover:underline text-xs font-semibold">Save</button>
                        <button onClick={() => setEditingId(null)} className="text-gray-500 hover:underline text-xs font-semibold">Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={p.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-3">
                        {p.debtor_name}
                        {p.invoice_number && <div className="text-xs text-gray-400">{p.invoice_number}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 max-w-[16rem] truncate" title={p.notes || ''}>{p.notes || '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{p.project_name || '—'}</td>
                      <td className="px-4 py-3">{formatDateDDMMYY(p.due_date)}</td>
                      <td className="px-4 py-3">{money(p.total_amount)}</td>
                      <td className="px-4 py-3 font-medium">{money(p.pending_amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded font-semibold ${AGING_BADGE[p.aging_bucket]}`}>
                          {AGING_LABELS[p.aging_bucket]}
                        </span>
                      </td>
                      <td className="px-4 py-3">{renderPromise(p)}</td>
                      <td className="px-4 py-3 text-gray-500">{p.responsible_employee_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded font-semibold ${STATUS_COLORS[p.status]}`}>
                          {STATUS_LABELS[p.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                        {p.status !== 'paid' && p.status !== 'written_off' && (
                          <button onClick={() => handleRecordPayment(p)} className="text-komal-blue hover:underline text-xs font-semibold">Record</button>
                        )}
                        <button onClick={() => startEdit(p)} className="text-gray-600 hover:underline text-xs font-semibold">Edit</button>
                        {p.status !== 'paid' && p.status !== 'written_off' && (
                          <button onClick={() => handleWriteOff(p)} className="text-amber-600 hover:underline text-xs font-semibold">Write off</button>
                        )}
                        <button onClick={() => handleDelete(p)} className="text-red-500 hover:underline text-xs font-semibold">Remove</button>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentsPage;
